import React, { ChangeEvent, FC, useContext, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { IReviewComment } from '../../shared/interfaces/IReviewComment';
import { AuthContext } from '../../providers/AuthProvider';
import CommentingService from '../../services/CommentingService';
import CommentAiService, {
  IGenerateCommentOptions,
  IReviewMetadata,
} from '../../services/CommentAiService';
import { constants } from '../../constants/constants';
import { CommunityManagement } from '../../shared/interfaces/ICommunityManagement';
import { useTranslation } from 'react-i18next';
import GMBService from '../../services/GMBService';
import CommentStatusSelector from '../CommentStatusSelector/CommentStatusSelector';

interface Props {
  id: string;
  reviewId: string;
  locationId: string;
  input: string;
  reviewComment?: IReviewComment;
  communityManagement: CommunityManagement;
  platform: string;
  reviewMetadata: IReviewMetadata;
  noReviewText: boolean;
  onSaveSuccess: (reply: IReviewComment) => void;
}

const ReviewReplyGMB: FC<Props> = ({
  id,
  locationId,
  reviewId,
  input,
  reviewComment,
  communityManagement,
  onSaveSuccess,
  reviewMetadata,
  noReviewText,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hasRole } = useContext(AuthContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reply, setReply] = useState<IReviewComment>(reviewComment || { text: '', status: 'OPEN' });
  const [deleted, setDeleted] = useState<boolean>(false);

  const handlePostReply = async () => {
    let response = await GMBService.replyToReview(locationId, reviewId, reply.text, setLoading);

    if (!response) {
      setErrorMessage(t('reply_failed'));
      return;
    }

    response = await CommentingService.saveReviewComment(id, 'DONE', reply.text, setLoading);

    if (!response) return;

    setReply((prevState) => ({
      ...prevState,
      status: 'DONE',
    }));
    setDeleted(false);
    // The list row is updated from the value handed to onSaveSuccess, and `reply` still carries the status
    // from before setReply — passing it would leave a published reply showing OPEN until a reload (772363).
    onSaveSuccess({ ...reply, status: 'DONE' });
  };

  const handleGenerateReply = async () => {
    const generateCommentOptions: IGenerateCommentOptions = {
      language: communityManagement.languagePreference,
      tonality: constants.tonality[communityManagement.tonality.preference],
      duzen: communityManagement.pronoun.preference === 0,
      review: input,
      useCustomerName: communityManagement.useUsername,
      salutation: communityManagement.salutationPrefix,
      signature: communityManagement.greeting,
      additionalPrompt: communityManagement.additionalPrompt,
      reviewMetadata: reviewMetadata,
      noReviewText,
    };
    const response = await CommentAiService.generateComment(generateCommentOptions, setLoading);

    if (!response) return;

    setReply((prevState) => ({
      ...prevState,
      text: response,
    }));
  };

  const handleSaveReply = async () => {
    const response = await CommentingService.saveReviewComment(
      id,
      reply.status,
      reply.text,
      setLoading
    );

    if (!response) return;

    setDeleted(false);
    onSaveSuccess(reply);
  };

  const handleDeleteReply = async () => {
    const response = await GMBService.deleteReviewReply(locationId, reviewId, setLoading);
    if (!response) {
      setErrorMessage(t('reply_delete_failed'));
      return;
    }
    await CommentingService.deleteReviewComment(id, setLoading);

    setReply({ text: '', status: '' });
    setDeleted(true);
  };

  return (
    <>
      <Box sx={{ mt: theme.spacing(2) }}>
        <TextField
          disabled={loading}
          fullWidth
          label={t('comment')}
          multiline
          rows={8}
          InputLabelProps={{ shrink: !!reply.text }}
          value={reply.text}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setReply((prevState) => ({
              ...prevState,
              text: event.target.value,
            }));
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', my: theme.spacing(2) }}>
          {hasRole('can-use-ai') && (
            <Button
              disabled={loading}
              variant="contained"
              onClick={async () => await handleGenerateReply()}
              sx={{ mr: theme.spacing(1) }}
            >
              {t('generate')}
            </Button>
          )}
          <Button
            disabled={loading}
            variant="contained"
            onClick={async () => await handleSaveReply()}
          >
            {t('save')}
          </Button>
          <Button
            disabled={loading || !reply.text}
            variant="contained"
            onClick={async () => await handlePostReply()}
            sx={{ ml: 1 }}
          >
            {t('to_comment')}
          </Button>
          <Button
            disabled={loading}
            variant="contained"
            color="warning"
            onClick={() => {
              setReply({ text: '', status: '' });
            }}
            sx={{ ml: 1 }}
          >
            {t('reset')}
          </Button>
          <Button
            disabled={loading || !reviewComment?.text || !reviewComment?.status || deleted}
            variant="contained"
            color="error"
            onClick={async () => await handleDeleteReply()}
            sx={{ ml: 1 }}
          >
            {t('delete')}
          </Button>
          {loading && (
            <CircularProgress
              sx={{ ml: theme.spacing(1) }}
              size={24}
            />
          )}
          <CommentStatusSelector
            disabled={loading}
            value={reply.status || 'OPEN'}
            onChange={(event: SelectChangeEvent) => {
              setReply((prevState) => ({
                ...prevState,
                status: event.target.value,
              }));
            }}
          />
        </Box>
        {hasRole('can-use-ai') && (
          <Typography
            sx={{ marginLeft: '0.5rem', marginTop: '0rem' }}
            variant="caption"
          >
            {t('ai_disclaimer_reply')}
          </Typography>
        )}
      </Box>
      <Snackbar
        open={errorMessage !== null}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setErrorMessage(null)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ReviewReplyGMB;
